import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  Grid,
  Button,
  Divider,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { ReusableDataGrid } from '@/components/common/ReusableDataGrid';
import { Fraternity } from '@/constants/types';
import { fraternityService } from '@/services';
import type { MRT_ColumnDef } from 'material-react-table';
import moment from 'moment';
import toast from 'react-hot-toast';

export const FraternityPage: React.FC = () => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [fraternities, setFraternities] = useState<Fraternity[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch fraternities from API
  const fetchFraternities = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fraternityService.list({
        sortDir: 'asc',
        limit: paginationModel.pageSize,
        offset: paginationModel.page ,
        search_string: searchTerm,
        sortBy: 'sequenceNumber',
      });

      if (response.detail && response.detail[0]) {
        setFraternities(response.detail[0].paginatedResults);
        setTotalCount(response.detail[0].totalCount[0]?.count || 0);
      }
    } catch (error) {
      console.error('Error fetching fraternities:', error);
      toast.error('Failed to fetch fraternities');
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, searchTerm]);

  useEffect(() => {
    fetchFraternities();
  }, [fetchFraternities]);

  const handleClearSearch = () => {
    setSearchTerm('');
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const columns: MRT_ColumnDef<Fraternity>[] = [
    // {
    //   accessorKey: 'id',
    //   header: 'ID',
    //   size: 100,
    // },

    

     {
      accessorKey: 'sequenceNumber',
      header: 'Sequence Number',
      size: 220,
      Cell: ({ cell }) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {cell.getValue<string>() || 'N/A'}
          </Typography>

        </Box>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Fraternity Name',
      size: 220,
      Cell: ({ cell, row }) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {cell.getValue<string>() || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            No. {row.original.number || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'dateOfWarrant',
      header: 'Date of Warrant',
      size: 140,
      Cell: ({ cell }) => {
        const date = cell.getValue<string>();
        return (
          <Typography variant="body2">
            {date ? moment(date).format('DD-MM-YYYY') : 'N/A'}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'regularMeeting',
      header: 'Regular Meeting',
      size: 150,
      Cell: ({ cell }) => (
        <Typography variant="body2">
          {cell.getValue<string>() || 'N/A'}
        </Typography>
      ),
    },
    {
      accessorKey: 'installationMeeting',
      header: 'Installation Meeting',
      size: 170,
      Cell: ({ cell }) => (
        <Typography variant="body2">
          {cell.getValue<string>() || 'N/A'}
        </Typography>
      ),
    },
    {
      accessorKey: 'regularMonths',
      header: 'Regular Months',
      size: 250,
      Cell: ({ cell }) => {
        const months = cell.getValue<string[]>() || [];
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {months.map((month, index) => (
              <Chip
                key={index}
                label={month ? month.charAt(0).toUpperCase() + month.slice(1) : ''}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))}
          </Box>
        );
      },
    },
    {
      accessorKey: 'installationMonth',
      header: 'Installation Month',
      size: 150,
      Cell: ({ cell }) => {
        const month = cell.getValue<string>() || '';
        return (
          <Chip
            label={month ? month.charAt(0).toUpperCase() + month.slice(1) : 'N/A'}
            size="small"
            color="secondary"
            variant="filled"
          />
        );
      },
    },
    {
      accessorKey: 'regularPreference',
      header: 'Regular Preference',
      size: 150,
      Cell: ({ cell }) => {
        const preference = cell.getValue<string>() || '';
        const colorMap: Record<string, 'default' | 'primary' | 'success' | 'info' | 'warning'> = {
          morning: 'info',
          afternoon: 'warning',
          evening: 'primary',
          night: 'default',
        };
        return (
          <Chip
            label={preference ? preference.charAt(0).toUpperCase() + preference.slice(1) : 'N/A'}
            size="small"
            color={colorMap[preference] || 'default'}
          />
        );
      },
    },
    {
      accessorKey: 'installationPreference',
      header: 'Installation Preference',
      size: 170,
      Cell: ({ cell }) => {
        const preference = cell.getValue<string>() || '';
        const colorMap: Record<string, 'default' | 'primary' | 'success' | 'info' | 'warning'> = {
          morning: 'info',
          afternoon: 'warning',
          evening: 'primary',
          night: 'default',
        };
        return (
          <Chip
            label={preference ? preference.charAt(0).toUpperCase() + preference.slice(1) : 'N/A'}
            size="small"
            color={colorMap[preference] || 'default'}
          />
        );
      },
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h5" component="h5" gutterBottom>
          Fraternity Management
        </Typography>
        <Divider />
      </Box>

      {/* Search Filter */}
      <Grid container spacing={2} alignItems="center" mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label="Search Fraternity"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPaginationModel({ ...paginationModel, page: 0 });
            }}
            placeholder="Search by name"
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 1 }}>
          <Button
            variant="text"
            onClick={handleClearSearch}
            startIcon={<ClearIcon />}
            title="Clear Search"
            sx={{
              '& .MuiButton-startIcon': {
                mr: 0,
                ml: 0,
                display: 'flex',
                justifyContent: 'end',
              },
            }}
            disabled={searchTerm === ''}
          />
        </Grid>
      </Grid>

      {/* Data Grid */}
      <ReusableDataGrid<Fraternity>
        columns={columns}
        rows={fraternities}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationData={{
          totalRecords: totalCount,
        }}
        enableServerSidePagination={true}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableSorting={true}
        getRowId={(row) => row._id}
        showSerialNumber={true}
      />
    </Box>
  );
};
